// application/coverageChange.js

/**
 * The vocabulary three parties share: the unit of work produces changes, `commitCoverages` scopes
 * and routes them, and a CoverageRepository applies them.
 *
 * It lives on its own rather than inside any one of those so none of them owns it — otherwise the
 * repository adapter ends up importing a concrete application service just to read an enum it was
 * handed. (Infrastructure depending on application is the dependency rule working, not an
 * exception to it; the point here is only which module, not which layer.)
 *
 * `CoverageIntent` names a WRITE OPERATION and has nothing to do with `BillingIntent`, which is a
 * domain decision not to bill. The collision is unfortunate and the two are never mixed: one is a
 * verb the repository performs, the other a record it stores.
 *
 * @typedef {'create' | 'update' | 'delete'} CoverageIntent
 *
 * @typedef {Object} CoverageChange
 * @property {CoverageIntent} intent
 * @property {import('../domain/model/Coverage.types').Coverage} coverage
 *
 * Billing-intent changes use their OWN verbs rather than borrowing create/update/delete. Calling a
 * release a "delete" is what led the first version of this to tombstone released pauses — which
 * asserted the decision never happened and erased it on the next pause. Pausing writes a new
 * record; releasing closes the open one. Neither is a deletion.
 *
 * @typedef {'pause' | 'release'} BillingIntentVerb
 *
 * @typedef {Object} BillingIntentChange
 * @property {BillingIntentVerb} verb
 * @property {import('../domain/model/BillingIntent.types').BillingIntent} billingIntent
 */

/** @type {Record<string, CoverageIntent>} */
export const INTENT = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
};

/** @type {Record<string, BillingIntentVerb>} */
export const BILLING_INTENT = {
  PAUSE: 'pause',
  RELEASE: 'release',
};
